// NEW COMPONENT - Enhanced Enquiry Modal with Cost Estimation Calculator
// This is a completely new addition and does not modify any existing UI

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Chip,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
  Paper,
  Collapse,
  ListItemIcon
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Calculator as CalculatorIcon,
  Print as PrintIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  LocalHospital,
  MonetizationOn,
  Schedule,
  CheckCircle
} from '@mui/icons-material';

// Sample service data - In real app, this would come from API
const serviceCategories = {
  consultation: {
    name: 'Consultation',
    services: [
      { id: 'gen_consultation', name: 'General Consultation', price: 500, duration: '30 min' },
      { id: 'specialist_consultation', name: 'Specialist Consultation', price: 800, duration: '45 min' },
      { id: 'follow_up', name: 'Follow-up Consultation', price: 300, duration: '20 min' }
    ]
  },
  pathology: {
    name: 'Pathology Tests',
    services: [
      { id: 'blood_test', name: 'Complete Blood Count (CBC)', price: 300, duration: '1 day' },
      { id: 'lipid_profile', name: 'Lipid Profile', price: 400, duration: '1 day' },
      { id: 'diabetes_test', name: 'HbA1c (Diabetes)', price: 350, duration: '1 day' },
      { id: 'thyroid_test', name: 'Thyroid Function Test', price: 450, duration: '1 day' },
      { id: 'liver_function', name: 'Liver Function Test', price: 500, duration: '1 day' }
    ]
  },
  radiology: {
    name: 'Radiology/Imaging',
    services: [
      { id: 'xray_chest', name: 'X-Ray Chest', price: 200, duration: '15 min' },
      { id: 'ultrasound', name: 'Ultrasound Abdomen', price: 800, duration: '30 min' },
      { id: 'ct_scan', name: 'CT Scan', price: 2500, duration: '45 min' },
      { id: 'mri_scan', name: 'MRI Scan', price: 4000, duration: '60 min' }
    ]
  },
  pharmacy: {
    name: 'Pharmacy',
    services: [
      { id: 'generic_meds', name: 'Generic Medications (Estimated)', price: 200, duration: 'Immediate' },
      { id: 'branded_meds', name: 'Branded Medications (Estimated)', price: 500, duration: 'Immediate' },
      { id: 'supplements', name: 'Health Supplements', price: 300, duration: 'Immediate' }
    ]
  }
};

const packageDeals = [
  {
    id: 'basic_checkup',
    name: 'Basic Health Checkup',
    services: ['gen_consultation', 'blood_test', 'xray_chest'],
    originalPrice: 1000,
    discountedPrice: 850,
    discount: 15
  },
  {
    id: 'comprehensive_checkup',
    name: 'Comprehensive Health Checkup',
    services: ['specialist_consultation', 'blood_test', 'lipid_profile', 'diabetes_test', 'ultrasound'],
    originalPrice: 2350,
    discountedPrice: 1999,
    discount: 15
  },
  {
    id: 'diabetes_package',
    name: 'Diabetes Management Package',
    services: ['specialist_consultation', 'diabetes_test', 'lipid_profile'],
    originalPrice: 1600,
    discountedPrice: 1299,
    discount: 19
  }
];

const EnhancedEnquiryModal = ({ 
  open, 
  onClose, 
  initialService = null,
  patientInfo = null 
}) => {
  // Debug logging
  console.log('EnhancedEnquiryModal props:', { open, onClose: typeof onClose, initialService, patientInfo });

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customServices, setCustomServices] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: patientInfo?.name || '',
    phone: patientInfo?.phone || '',
    email: patientInfo?.email || '',
    age: patientInfo?.age || '',
    gender: patientInfo?.gender || ''
  });
  const [estimationType, setEstimationType] = useState('individual'); // 'individual' or 'package'
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (initialService) {
      setSelectedPackage(initialService);
    }
  }, [initialService]);

  const handleClose = () => {
    console.log('handleClose called, onClose type:', typeof onClose);
    if (typeof onClose === 'function') {
      console.log('Calling onClose function');
      onClose();
    } else {
      console.error('onClose is not a function');
    }
  };

  const handleServiceToggle = (categoryId, serviceId) => {
    const service = serviceCategories[categoryId].services.find(s => s.id === serviceId);
    const serviceWithCategory = { ...service, category: categoryId };
    
    const isSelected = customServices.some(s => s.id === serviceId);
    
    if (isSelected) {
      setCustomServices(prev => prev.filter(s => s.id !== serviceId));
    } else {
      setCustomServices(prev => [...prev, serviceWithCategory]);
      setSelectedPackage(null); // Clear package selection when individual service is selected
      setEstimationType('individual'); // Set estimation type to individual
    }
  };

  const handlePackageSelect = (packageId) => {
    if (selectedPackage === packageId) {
      setSelectedPackage(null);
      setEstimationType('individual');
    } else {
      setSelectedPackage(packageId);
      setEstimationType('package'); // Set estimation type to package when a package is selected
      setCustomServices([]); // Clear individual services when package is selected
    }
  };

  const calculateTotal = () => {
    if (selectedPackage) {
      const pkg = packageDeals.find(p => p.id === selectedPackage);
      if (pkg) {
        return {
          originalTotal: pkg.originalPrice,
          discountedTotal: pkg.discountedPrice,
          savings: pkg.originalPrice - pkg.discountedPrice,
          discount: pkg.discount
        };
      }
    }
    // For individual services or if no package found
    const total = customServices.reduce((sum, service) => sum + service.price, 0);
    return {
      originalTotal: total,
      discountedTotal: total,
      savings: 0,
      discount: 0
    };
  };

  const getSelectedPackageDetails = () => {
    if (!selectedPackage) return null;
    const pkg = packageDeals.find(p => p.id === selectedPackage);
    const packageServices = pkg.services.map(serviceId => {
      for (const category of Object.values(serviceCategories)) {
        const service = category.services.find(s => s.id === serviceId);
        if (service) return service;
      }
      return null;
    }).filter(Boolean);
    
    return { ...pkg, services: packageServices };
  };

  const calculateEstimate = () => {
    let total = 0;
    let services = [];

    // Check if we have a selected package
    if (selectedPackage) {
      const pkg = packageDeals.find(p => p.id === selectedPackage);
      if (pkg) {
        total = pkg.discountedPrice;
        services = pkg.services.map(serviceId => {
          for (const category of Object.values(serviceCategories)) {
            const service = category.services.find(s => s.id === serviceId);
            if (service) return service;
          }
          return null;
        }).filter(Boolean);
      }
    } else {
      // Calculate total for individual services
      total = customServices.reduce((sum, service) => sum + service.price, 0);
      services = customServices;
    }

    const tax = total * 0.18; // 18% GST
    const finalTotal = total + tax;

    setEstimate({
      subtotal: total,
      tax: tax,
      total: finalTotal,
      services: services,
      packageName: selectedPackage ? packageDeals.find(p => p.id === selectedPackage)?.name : null,
      generatedAt: new Date().toISOString()
    });
  };

  const handleShareWhatsApp = () => {
    if (!estimate) return;

    const estimateText = generateEstimateText();
    const whatsappUrl = `https://wa.me/${customerInfo.phone}?text=${encodeURIComponent(estimateText)}`;
    window.open(whatsappUrl, '_blank');
    setSnackbar({ open: true, message: 'WhatsApp opened with estimate', severity: 'success' });
  };

  const handleShareEmail = () => {
    if (!estimate) return;

    const subject = `HMIS Treatment Cost Estimate - ${customerInfo.name || 'Patient'}`;
    const body = `Dear ${customerInfo.name || 'Patient'},\n\n` +
      `Please find your treatment cost estimate below:\n\n` +
      `Patient Details:\n` +
      `Name: ${customerInfo.name || 'N/A'}\n` +
      `Age: ${customerInfo.age || 'N/A'}\n` +
      `Contact: ${customerInfo.phone || 'N/A'}\n\n` +
      `Treatment Details:\n` +
      `${selectedPackage && getSelectedPackageDetails() ? `Package: ${getSelectedPackageDetails().name}\n` : ''}` +
      `Subtotal: ₹${estimate.subtotal.toLocaleString()}\n` +
      `Tax (18% GST): ₹${estimate.tax.toLocaleString()}\n` +
      `Total Amount: ₹${estimate.total.toLocaleString()}\n\n` +
      `Generated on: ${new Date().toLocaleDateString()}\n\n` +
      `Please contact us for appointment booking or any queries.\n\n` +
      `Best regards,\nHMIS Hospital Team`;

    const mailtoUrl = `mailto:${customerInfo.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    setSnackbar({ open: true, message: 'Email client opened', severity: 'success' });
  };

  const handlePrint = () => {
    if (!estimate) return;

    const printContent = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2>HMIS Hospital - Treatment Cost Estimate</h2>
        <hr>
        <h3>Patient Information</h3>
        <p><strong>Name:</strong> ${customerInfo.name || 'N/A'}</p>
        <p><strong>Age:</strong> ${customerInfo.age || 'N/A'}</p>
        <p><strong>Contact:</strong> ${customerInfo.phone || 'N/A'}</p>
        <p><strong>Email:</strong> ${customerInfo.email || 'N/A'}</p>
        
        <h3>Treatment Details</h3>
        ${selectedPackage && getSelectedPackageDetails() ? `<p><strong>Package:</strong> ${getSelectedPackageDetails().name}</p>` : ''}
        
        <h3>Cost Breakdown</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="border-bottom: 1px solid #ccc;">
            <td style="padding: 8px;"><strong>Subtotal</strong></td>
            <td style="padding: 8px; text-align: right;">₹${estimate.subtotal.toLocaleString()}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ccc;">
            <td style="padding: 8px;"><strong>Tax (18% GST)</strong></td>
            <td style="padding: 8px; text-align: right;">₹${estimate.tax.toLocaleString()}</td>
          </tr>
          <tr style="border-bottom: 2px solid #000;">
            <td style="padding: 8px;"><strong>Total Amount</strong></td>
            <td style="padding: 8px; text-align: right;"><strong>₹${estimate.total.toLocaleString()}</strong></td>
          </tr>
        </table>
        
        <p style="margin-top: 20px;"><small>Generated on: ${new Date().toLocaleDateString()}</small></p>
        <p><small>This is an estimated cost. Final charges may vary based on actual treatment.</small></p>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    
    setSnackbar({ open: true, message: 'Estimate sent to printer', severity: 'success' });
  };

  const generateEstimateText = () => {
    const total = calculateTotal();
    const packageDetails = getSelectedPackageDetails();
    
    let text = `🏥 HMIS Hospital - Cost Estimate\n\n`;
    text += `👤 Patient: ${customerInfo.name}\n`;
    text += `📱 Phone: ${customerInfo.phone}\n`;
    if (customerInfo.email) text += `📧 Email: ${customerInfo.email}\n`;
    text += `\n`;
    
    if (selectedPackage && packageDetails) {
      text += `📦 Package: ${packageDetails.name}\n`;
      text += `💰 Package Price: ₹${packageDetails.discountedPrice}\n`;
      text += `💵 Original Price: ₹${packageDetails.originalPrice}\n`;
      text += `🎯 Savings: ₹${total.savings} (${total.discount}% off)\n\n`;
      text += `📋 Includes:\n`;
      packageDetails.services.forEach(service => {
        text += `• ${service.name} (₹${service.price})\n`;
      });
    } else {
      text += `📋 Selected Services:\n`;
      customServices.forEach(service => {
        text += `• ${service.name} - ₹${service.price}\n`;
      });
      text += `\n💰 Total Amount: ₹${total.discountedTotal}\n`;
    }
    
    text += `\n📞 Contact: +91-XXXXXXXXXX`;
    text += `\n🌐 Visit: www.hmishospital.com`;
    
    return text;
  };

  const total = calculateTotal();
  const packageDetails = getSelectedPackageDetails();

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospital color="primary" />
          <Typography variant="h6">Treatment Cost Estimator</Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
              <Tab label="Treatment Packages" />
              <Tab label="Custom Services" />
              <Tab label="Patient Info" />
            </Tabs>
          </Box>

          {/* Treatment Packages Tab */}
          {selectedTab === 0 && (
            <Grid container spacing={2}>
              {packageDeals.map(pkg => (
                <Grid item xs={12} md={6} key={pkg.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedPackage === pkg.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      '&:hover': { boxShadow: 3 },
                      position: 'relative'
                    }}
                    onClick={() => handlePackageSelect(pkg.id)}
                  >
                    {pkg.id === 'basic_checkup' && (
                      <Chip
                        label="Popular"
                        color="primary"
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                    )}
                    
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {pkg.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h5" color="primary">
                          ₹{pkg.discountedPrice}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                        >
                          ₹{pkg.originalPrice}
                        </Typography>
                        <Chip 
                          label={`${pkg.discount}% OFF`} 
                          color="success" 
                          size="small" 
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Save ₹{pkg.originalPrice - pkg.discountedPrice}
                      </Typography>
                      
                      <Button
                        variant="text"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedCard(expandedCard === pkg.id ? null : pkg.id);
                        }}
                        endIcon={<ExpandMoreIcon sx={{ 
                          transform: expandedCard === pkg.id ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s'
                        }} />}
                      >
                        View Details
                      </Button>
                      
                      <Collapse in={expandedCard === pkg.id}>
                        <List dense sx={{ mt: 1 }}>
                          {pkg.services.map((serviceId, index) => {
                            const service = Object.values(serviceCategories)
                              .flatMap(cat => cat.services)
                              .find(s => s.id === serviceId);
                            return service ? (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckCircle fontSize="small" color="success" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={service.name}
                                  secondary={`₹${service.price} - ${service.duration}`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ) : null;
                          })}
                        </List>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Custom Services Tab */}
          {selectedTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Add Individual Services
              </Typography>
              {Object.entries(serviceCategories).map(([categoryId, category]) => (
                <Accordion key={categoryId} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">{category.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {category.services.map(service => (
                        <Grid item xs={12} sm={6} md={4} key={service.id}>
                          <Card>
                            <CardContent sx={{ p: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {service.name}
                              </Typography>
                              <Typography color="text.secondary" variant="body2">
                                Duration: {service.duration}
                              </Typography>
                              <Typography variant="h6" color="primary">
                                ₹{service.price}
                              </Typography>
                              <Button
                                size="small"
                                variant={customServices.some(s => s.id === service.id) ? "contained" : "outlined"}
                                onClick={() => handleServiceToggle(categoryId, service.id)}
                                sx={{ mt: 1 }}
                              >
                                {customServices.some(s => s.id === service.id) ? 'Added' : 'Add'}
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}

              {customServices.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Selected Services
                  </Typography>
                  <List>
                    {customServices.map(service => (
                      <ListItem key={service.id}>
                        <ListItemText
                          primary={service.name}
                          secondary={serviceCategories[service.category].name}
                        />
                        <Typography variant="h6" sx={{ mr: 2 }}>
                          ₹{service.price}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleServiceToggle(service.category, service.id)}
                        >
                          Remove
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}

          {/* Patient Info Tab */}
          {selectedTab === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Patient Name"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Age"
                  type="number"
                  value={customerInfo.age}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, age: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={customerInfo.gender}
                    label="Gender"
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, gender: e.target.value }))}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Cost Estimate */}
          {estimate && (
            <Paper sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
              <Typography variant="h6" gutterBottom>
                <MonetizationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                Cost Estimate
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">
                    Subtotal: <strong>₹{estimate.subtotal.toLocaleString()}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Tax (18% GST): <strong>₹{estimate.tax.toLocaleString()}</strong>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" color="primary">
                    Total: <strong>₹{estimate.total.toLocaleString()}</strong>
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<WhatsAppIcon />}
                      onClick={handleShareWhatsApp}
                      size="small"
                      sx={{ color: '#25D366', borderColor: '#25D366' }}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={handleShareEmail}
                      size="small"
                    >
                      Email
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={handlePrint}
                      size="small"
                    >
                      Print
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
          <Button
            variant="contained"
            onClick={calculateEstimate}
            disabled={!selectedPackage && customServices.length === 0}
          >
            Calculate Estimate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EnhancedEnquiryModal;
